import React, { useState, useEffect } from 'react';
import { twilioPhoneNumberService } from '../services/api';

// Common country codes
const countryCodes = [
  { code: '+852', name: 'Hong Kong (+852)' },
  { code: '+86', name: 'China (+86)' },
  { code: '+1', name: 'USA/Canada (+1)' },
  { code: '+44', name: 'UK (+44)' },
  { code: '+65', name: 'Singapore (+65)' },
  { code: '+81', name: 'Japan (+81)' },
  { code: '+82', name: 'South Korea (+82)' },
  { code: '+61', name: 'Australia (+61)' },
  { code: '+91', name: 'India (+91)' },
  { code: '+60', name: 'Malaysia (+60)' },
  { code: '+66', name: 'Thailand (+66)' },
  { code: '+63', name: 'Philippines (+63)' }
];

const PhoneNumberInput = ({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  error,
  label = "Phone Number",
  showAvailableNumbers = false,
}) => {
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState([]);
  const [filteredPhoneNumbers, setFilteredPhoneNumbers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load available phone numbers when component mounts or when showAvailableNumbers changes
  useEffect(() => {
    if (showAvailableNumbers) {
      loadAvailablePhoneNumbers();
    }
  }, [showAvailableNumbers]);

  // Filter phone numbers when country code or phone number changes
  useEffect(() => {
    if (showAvailableNumbers && availablePhoneNumbers.length > 0) {
      let filtered = availablePhoneNumbers.filter(
        phone => phone.phone_number.startsWith(countryCode)
      );

      // If user is typing, also filter by the input
      if (phoneNumber && phoneNumber.length > 0) {
        const fullNumber = countryCode + phoneNumber;
        filtered = filtered.filter(phone =>
          phone.phone_number.includes(phoneNumber) ||
          phone.phone_number.includes(fullNumber)
        );
      }

      setFilteredPhoneNumbers(filtered);
    }
  }, [countryCode, phoneNumber, availablePhoneNumbers, showAvailableNumbers]);

  // Load available phone numbers from Twilio
  const loadAvailablePhoneNumbers = async () => {
    setLoading(true);
    try {
      // Use the new endpoint for unused phone numbers
      const data = await twilioPhoneNumberService.getUnusedPhoneNumbers();
      setAvailablePhoneNumbers(data);
    } catch (error) {
      console.error('Error loading available phone numbers:', error);
      setAvailablePhoneNumbers([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle country code change
  const handleCountryCodeChange = (e) => {
    onCountryCodeChange(e.target.value);

    // Clear phone number when country code changes
    onPhoneNumberChange('');

    // Close dropdown when country code changes
    setShowDropdown(false);
  };

  // Handle phone number change
  const handlePhoneNumberChange = (e) => {
    // Only allow digits for phone number
    const sanitizedValue = e.target.value.replace(/\D/g, '');
    onPhoneNumberChange(sanitizedValue);

    // Show dropdown if there are filtered numbers and user is typing
    if (showAvailableNumbers && filteredPhoneNumbers.length > 0) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  // Handle phone number selection from dropdown
  const handlePhoneNumberSelect = (selectedPhone) => {
    // Extract the number part without country code
    const numberWithoutCountryCode = selectedPhone.phone_number.substring(countryCode.length);
    onPhoneNumberChange(numberWithoutCountryCode);
    setShowDropdown(false);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (showAvailableNumbers && filteredPhoneNumbers.length > 0) {
      setShowDropdown(true);
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Delay closing dropdown to allow for clicks
    setTimeout(() => setShowDropdown(false), 200);
  };

  // Handle dropdown item click
  const handleDropdownItemClick = (phone) => {
    handlePhoneNumberSelect(phone);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="flex space-x-2">
        <div className="w-1/3">
          <select
            value={countryCode}
            onChange={handleCountryCodeChange}
            className="w-full p-2 border rounded"
          >
            {countryCodes.map((code) => (
              <option key={code.code} value={code.code}>
                {code.name}
              </option>
            ))}
          </select>
        </div>
        <div className="w-2/3">
          <input
            type="text"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className={`w-full p-2 border rounded ${error ? 'border-red-500' : ''}`}
            placeholder="Enter phone number (digits only)"
          />

          {/* Available phone numbers dropdown */}
          {showAvailableNumbers && showDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-3 text-sm text-gray-500 text-center">
                  Loading available numbers...
                </div>
              ) : filteredPhoneNumbers.length > 0 ? (
                <div>
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                    Available numbers for {countryCode} ({filteredPhoneNumbers.length})
                  </div>
                  {filteredPhoneNumbers.map((phone) => (
                    <div
                      key={phone.phone_number}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleDropdownItemClick(phone)}
                    >
                      <div className="font-medium text-sm">
                        {phone.phone_number}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-3 text-sm text-gray-500 text-center">
                  {phoneNumber && phoneNumber.length > 0
                    ? `No available numbers matching "${phoneNumber}" for ${countryCode}`
                    : `No available numbers for ${countryCode}`
                  }
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {showAvailableNumbers && (
        <p className="mt-1 text-xs text-gray-500">
          Type to see available phone numbers for the selected country code
        </p>
      )}

      {!showAvailableNumbers && (
        <p className="mt-1 text-xs text-gray-500">
          Enter the phone number without spaces or special characters
        </p>
      )}
    </div>
  );
};

export default PhoneNumberInput;
